import { fetch } from "@/lib/fetch";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import Cookies from "js-cookie";
import { useRouter } from "next/navigation";

import env from "@/env.mjs";

import { CentraJS } from "@frend-digital/centra-js-client";
import { CentraSelection } from "@frend-digital/centra-legacy/schemas/selection/selectionSchema";
import { useState } from "react";
import formatSelection from "../centra/formatting/formatSelection";
import optimisticUpdate from "../centra/optimisticUpdate";
import { CartItem } from "../centra/selection/selectionFormatter";
import usePersistedStore from "../stateManagement/persistedState/persistedStore";
import useLocation from "./useLocation";

export interface ICountry {
  currency: string;
  name: string;
  priceList: number;
  country: string;
  states?: IState[];
}
export interface IState {
  name: string;
  state: string;
}
interface IUser {
  country?: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  address1: string;
  city: string;
  zipCode: string;
  address2: string;
}
export type IUpdateUser = {
  country?: string;
  newEmail: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  address1: string;
  city: string;
  zipCode: string;
  address2: string;
};
export interface IShippingMethod {
  shippingMethod: string;
  name: string;
  price: string;
  priceAsNumber: number;
}

export interface ICart {
  token: string;
  loggedIn?: IUser;
  shippingMethods: IShippingMethod[];
  languages: [
    {
      language: string;
      name: string;
    },
  ];
  countries: ICountry[];
  location: {
    name: string;
    country: string;
    language: {
      language: string;
    };
    pricelist: number;
  };
  selection: ISelection;
  cartError?: String;
}
interface ISelection {
  items: ICartItem[];
  totals: {
    itemsTotalPrice: string;
    itemsTotalPriceAsNumber: number;
    shippingPrice: string;
    grandTotalPrice: string;
    totalDiscountPrice: string;
    totalQuantity: number;
    grandTotalPriceAsNumber: number;
  };
  discounts: {
    vouchers: [IVoucher];
  };
  centraCheckoutScript: string;
  currency: string;
}

export interface IVoucher {
  voucher: string;
  priceOff: string;
}

export interface ICartItem {
  id: any;
  item: string;
  size: string;
  line: string;
  quantity: number;
  totalPrice: string;
  priceEach: string;
  priceEachBeforeDiscountAsNumber: number;
  product: {
    productSku: any;
    tableMappings: any;
    name: string;
    variantName: string;
    media: { full: [string]; "thumb-4x5": [string] };
    sku: string;
    categoryName: string[];
    uri: string;
  };
}

export const getSelection = async (
  token: string,
  setToken: (token: string) => void
) => {
  const api = env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;
  const res = await axios.get(`${api}/selection/`, {
    headers: { "API-token": token || "" },
  });

  if (res.data === null) throw Error("Error getting selection");
  setToken(res.data.token);
  const formattedData = formatSelection(res.data);
  return formattedData;
};

const useLegacySelection = () => {
  const setToken = usePersistedStore((state) => state.setToken);
  const selection = useQuery({
    queryKey: ["selection"],

    queryFn: () => getSelection(usePersistedStore.getState().token, setToken),
  });

  const items = selection.data?.selection?.items ?? [];
  const summary = selection.data?.selection?.summary;
  const priceList = selection?.data?.location?.pricelist;
  const loggedIn = selection?.data?.loggedIn;
  const currency = selection?.data?.selection?.currency;

  const cart = {
    items: items,
    summary: summary,
    priceList: priceList,
    currency,
    loggedIn: loggedIn,
    PromoCodes: selection.data?.selection?.discounts,
  };

  return {
    cart,
    address: selection.data?.selection?.address,
    data: selection?.data,
    items: selection.data?.selection?.items,
    summary: selection.data?.selection?.summary,
    isLoading: selection?.isLoading,
    refetch: selection?.refetch,
    priceList: selection?.data?.location?.pricelist,
    loggedIn: selection?.data?.loggedIn as IUser | undefined,
  };
};

const addProductToCart = async (
  variables: { itemId: string; quantity?: number },
  customerToken: string
) => {
  const api = env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;

  const res = await axios.post(
    `${api}/items/${variables.itemId}/quantity/${variables.quantity ?? 1}`,
    {},
    { headers: { "API-token": customerToken || "" } }
  );

  if (res.data === null) throw Error("Error adding product to cart");
  return res.data;
};

const updateCartLine = async (
  variables: { lineId: string; quantity?: number },
  customerToken: string
) => {
  const api = env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;

  const res = await axios.post(
    `${api}/lines/${variables.lineId}/quantity/${variables.quantity ?? 1}`,
    {},
    { headers: { "API-token": customerToken || "" } }
  );

  if (res.data === null) throw Error("Error adding product to cart");
  return res.data;
};

export function useUpdateCartLine() {
  const queryClient = useQueryClient();
  if (!queryClient) console.error("No queryClient");

  const customerToken = usePersistedStore((state) => state.token);
  const { currency } = useLocation();
  const addToCart = useMutation({
    mutationKey: ["addToCart"],
    mutationFn: async (variables: { lineId: string; cartItem?: CartItem }) => {
      await queryClient.cancelQueries({ queryKey: ["selection"] });
      return await updateCartLine(variables, customerToken);
    },

    onMutate: async (variables) => {
      const oldData = queryClient.getQueryData(["selection"]);
      if (!oldData) queryClient.invalidateQueries({ queryKey: ["selection"] });

      // // Optimistically update to the new value
      queryClient.setQueryData(["selection"], (oldCart: any) => oldCart);
      //   optimisticAddToCart(
      //     oldCart,
      //     {
      //       itemId: variables.lineId
      //     },
      //     currency || ""
      //   )
      // );

      return { oldData };
    },
    onError: async (error, _variables, context) => {
      return queryClient.setQueryData(["selection"], context?.oldData);
    },
    onSettled: async () => {
      return queryClient.invalidateQueries({ queryKey: ["selection"] });
    },
  });
  return { addToCart };
}

export const useAddToCart = () => {
  const queryClient = useQueryClient();
  const customerToken = usePersistedStore((state) => state.token);

  const addToCart = useMutation({
    mutationFn: async (variables: { itemId: string; quantity?: number }) =>
      addProductToCart(variables, customerToken),
    onMutate: () => {
      CentraJS.suspend();
    },
    onError: async (error: Error) => {
      return error;
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(["selection"], formatSelection(data));
    },
    onSettled: () => {
      CentraJS.resume();
    },
  });

  return { addToCart };
};

const setQuantity = async (
  variables: {
    line: string;
    newQuantity: number;
  },
  customerToken: string
) => {
  CentraJS.suspend();
  const api = env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;
  const res = await fetch(
    `${api}/lines/${variables.line}/quantity/${variables.newQuantity}`,
    {
      method: "PUT",
      headers: { "API-token": customerToken || "" },
    }
  )
    .then((res) => {
      if (!res.ok) throw Error("Error updating quantity");
      return res.json();
    })
    .then((data) => {
      if (data === null) throw Error("Error updating quantity");
      return data;
    })
    .catch((err) => {
      CentraJS.resume();
      throw err;
    });

  CentraJS.resume();

  return res;
};

export const useChangeQuantity = () => {
  const queryClient = useQueryClient();
  const customerToken = usePersistedStore((state) => state.token);
  const changeQuantity = useMutation({
    mutationFn: (variables: { line: string; newQuantity: number }) =>
      setQuantity(variables, customerToken),
    onMutate: async (variables) => {
      CentraJS.suspend();
      await queryClient.cancelQueries({ queryKey: ["selection"] });

      // Snapshot the previous value
      const oldData = queryClient.getQueryData(["selection"]);

      // Optimistically update to the new value
      queryClient.setQueryData(["selection"], (oldCart: any) =>
        optimisticUpdate(oldCart, variables)
      );

      // Return a context object with the snapshotted value
      return { oldData };
    },
    onError: (error, _variables, context) => {
      if (context?.oldData) {
        queryClient.setQueryData(["selection"], context?.oldData);
        console.warn("error", error);
      }
    },
    onSettled: () => {
      CentraJS.resume();
      queryClient.invalidateQueries({ queryKey: ["selection"] });
    },
  });

  return { changeQuantity };
};

const setPromo = async (
  variables: { code: string; remove: boolean },
  customerToken: string
) => {
  const api = env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;
  CentraJS.suspend();
  const res = await fetch(`${api}/vouchers`, {
    method: !variables.remove ? "POST" : "DELETE",
    headers: { "API-token": customerToken || "" },
    body: JSON.stringify({
      voucher: variables.code,
    }),
  })
    .then((res) => {
      if (!res.ok) throw Error("Invalid promo code");
      return res.json();
    })
    .then((data) => {
      if (data === null || Object.prototype.hasOwnProperty.call(data, "errors"))
        throw Error("Invalid promo code");
      CentraJS.resume();
      return data;
    })
    .catch((err) => {
      CentraJS.resume();
      throw err;
    });

  return res;
};

export const usePromoMutation = () => {
  const queryClient = useQueryClient();
  const customerToken = usePersistedStore((state) => state.token);
  const promo = useMutation({
    mutationFn: (variables: { code: string; remove: boolean }) =>
      setPromo(variables, customerToken),
    onError: async (error: Error) => {
      return error;
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(["selection"], formatSelection(data));
    },
  });

  return { promo };
};

const loginUser = async (
  variables: { email: string; password: string },
  customerToken: string
) => {
  const apiUrl = env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;

  const { email, password } = variables;
  const res = await axios.post(
    `${apiUrl}/login/${email}`,
    {
      password,
    },
    { headers: { "API-token": customerToken || "" } }
  );

  return res.data;
};

export const useLoginUser = () => {
  const queryClient = useQueryClient();
  const customerToken = usePersistedStore((state) => state.token);
  const router = useRouter();

  const login = useMutation({
    mutationFn: (variables: { email: string; password: string }) =>
      loginUser(variables, customerToken),
    onMutate: () => {
      CentraJS.suspend();
    },
    onError: async (error: Error) => {
      return error;
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(["selection"], formatSelection(data));

      Cookies.set("token", data.token, {
        expires: 365,
        secure: true,
        sameSite: "strict",
      });
      router.refresh();
    },
    onSettled: () => {
      CentraJS.resume();
    },
  });

  return { login };
};

const signUpUser = async (
  customerToken: string,
  variables: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    newsletter?: boolean;
  }
) => {
  const apiUrl = env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;
  const { email, password, lastName, firstName, newsletter } = variables;
  if (newsletter) {
    await axios.post(`${apiUrl}/newsletter-subscription`, {
      email,
    });
  }

  const res = await axios.post(
    `${apiUrl}/register/`,
    {
      email,
      password,
      firstName,
      lastName,
      newsletter,
    },
    { headers: { "API-token": customerToken || "" } }
  );
  return res.data;
};

export interface ISignupError extends AxiosError {}

export const useSignUpUser = () => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const customerToken = usePersistedStore((state) => state.token);
  const signUp = useMutation({
    mutationFn: (variables: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      newsletter?: boolean;
    }) => signUpUser(customerToken, variables),
    onMutate: () => {
      CentraJS.suspend();
    },
    onError: async (error: ISignupError) => {
      return error;
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(["selection"], formatSelection(data));
      Cookies.set("token", data.token, {
        expires: 365,
        secure: true,
        sameSite: "strict",
      });
      router.refresh();
    },
    onSettled: () => {
      CentraJS.resume();
    },
  });

  return { signUp };
};

const resetPassword = async (variables: {
  newPassword: string;
  confirmPassword: string;
  i: any;
  id: any;
}) => {
  const apiUrl = env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;
  const { newPassword, confirmPassword, i, id } = variables;

  if (newPassword !== confirmPassword) throw Error("Passwords do not match");

  const res = await axios.post(
    `${apiUrl}/password-reset/`,
    {
      newPassword,
      i,
      id,
    },
    {
      headers: {
        "API-token": usePersistedStore.getState().token,
      },
    }
  );

  return res.data;
};

export const useResetPassword = () => {
  const queryClient = useQueryClient();
  const setNewPassword = useMutation({
    mutationFn: resetPassword,
    onMutate: () => {
      CentraJS.suspend();
    },
    onError: async (error: Error) => {
      return error;
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(["selection"], formatSelection(data));
    },
    onSettled: () => {
      CentraJS.resume();
    },
  });

  return { setNewPassword };
};

const resetPasswordEmail = async (variables: {
  email: string;
  linkUri: string;
}) => {
  const { email, linkUri } = variables;
  const apiUrl = env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;
  const res = await axios.post(`${apiUrl}/password-reset-email/${email}`, {
    linkUri,
  });

  return res.data;
};

export const useResetPasswordEmail = () => {
  const queryClient = useQueryClient();
  const resetPassword = useMutation({
    mutationFn: resetPasswordEmail,
    onMutate: () => {
      CentraJS.suspend();
    },
    onError: async (error: Error) => {
      return error;
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(["selection"], formatSelection(data));
      queryClient.invalidateQueries({
        queryKey: ["selection"],
      });
    },
    onSettled: () => {
      CentraJS.resume();
    },
  });

  return { resetPassword };
};

const updateUser = async (variables: IUpdateUser, customerToken: string) => {
  const apiUrl = env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;

  const res = await fetch(`${apiUrl}/customer/update`, {
    body: JSON.stringify(variables),
    method: "PUT",
    headers: { "API-token": customerToken || "" },
  });
  if (!res.ok) {
    throw new Error("Error updating user" + res?.status + res?.statusText);
  }
  const data = await res.json();
  return data;
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const customerToken = usePersistedStore((state) => state.token);
  const [error, setError] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  const update = useMutation({
    mutationFn: (variables: IUpdateUser) =>
      updateUser(variables, customerToken),
    onMutate: () => {
      CentraJS.suspend();
    },
    onError: async (error: Error) => {
      setError(true);
      setSuccess(false);

      return error;
    },
    onSuccess: async (data: CentraSelection) => {
      queryClient.setQueryData(["selection"], formatSelection(data));
      setError(false);
      setSuccess(true);
    },
    onSettled: () => {
      CentraJS.resume();
    },
  });

  return { update, error, success };
};

const changePassword = async (
  variables: {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  },
  customerToken: string
) => {
  const apiUrl = env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;

  const { oldPassword, newPassword, confirmPassword } = variables;

  if (newPassword !== confirmPassword) throw Error("Passwords do not match");

  const res = await axios.put(
    `${apiUrl}/password`,
    {
      password: oldPassword,
      newPassword,
    },
    { headers: { "API-token": customerToken || "" } }
  );

  return res.data;
};

export const useUpdatePassword = () => {
  const queryClient = useQueryClient();
  const customerToken = usePersistedStore((state) => state.token);
  const updatePassword = useMutation({
    mutationFn: (variables: {
      oldPassword: string;
      newPassword: string;
      confirmPassword: string;
    }) => changePassword(variables, customerToken),
    onMutate: () => {
      CentraJS.suspend();
    },
    onError: async (error: Error) => {
      console.log("Error updating password! ", error);
      return error;
    },
    onSuccess: async (data) => {
      console.log("Success updating password!");
      queryClient.setQueryData(["selection"], formatSelection(data));
    },
    onSettled: () => {
      CentraJS.resume();
    },
  });
  return { updatePassword };
};

const logOutUser = async (customerToken: string) => {
  const apiUrl = env.NEXT_PUBLIC_CENTRA_CHECKOUT_API;
  const res = await axios.post(
    `${apiUrl}/logout/`,
    {},
    {
      headers: { "API-token": customerToken || "" },
    }
  );

  return res.data;
};

export const useLogOut = () => {
  const queryClient = useQueryClient();
  const customerToken = usePersistedStore((state) => state.token);
  const router = useRouter();

  const logOut = useMutation({
    mutationFn: () => logOutUser(customerToken),
    onMutate: () => {
      CentraJS.suspend();
    },
    onError: async (error: Error) => {
      return error;
    },
    onSuccess: async (data) => {
      queryClient.setQueryData(["selection"], formatSelection(data));

      Cookies.remove("token");
      router.push("/");
    },
    onSettled: () => {
      CentraJS.resume();
    },
  });

  return { logOut };
};

export default useLegacySelection;

export type UseLegacySelectionReturn = Awaited<ReturnType<typeof getSelection>>;
